import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSocket } from '../context/SocketContext';
import { activityService } from '../services/activityService';
import {
  canonicalEventTypeForDisplay,
  getActivityEventStatLabel,
} from '../utils/activityEventLabels';
import { shouldExcludeFromActivityStats } from '../utils/activityFeedExclude';
import { TrendingUp, Users, Activity } from 'lucide-react';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/Loading.json';

const ActivityStatsWidget = ({ dateRange = 'today', onSummaryChange }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalUniqueUsersFromBackend, setTotalUniqueUsersFromBackend] = useState(null);
  const { socket } = useSocket();
  
  // ✅ Pending request kontrolü - Duplicate request'leri önlemek için
  const fetchStatsRef = useRef(null);

  /**
   * Aynı olayın farklı kodlarla (login + user_login vb.) iki satır gelmesini önler;
   * çakışan alias'larda (login + user_login vb.) şişmeyi engellemek için
   * hem count hem uniqueUsers için üst sınır olarak max alır.
   */
  const mergedStats = useMemo(() => {
    if (!Array.isArray(stats) || stats.length === 0) return [];
    const map = new Map();
    for (const stat of stats) {
      const rawType = stat.eventType;
      if (shouldExcludeFromActivityStats(rawType)) continue;
      const key = canonicalEventTypeForDisplay(rawType);
      const canonical = key || String(rawType || 'unknown');
      const count = Number(stat.count) || 0;
      const uniqueUsers = Number(stat.uniqueUsers) || 0;
      const prev = map.get(canonical);
      if (!prev) {
        map.set(canonical, { eventType: canonical, count, uniqueUsers });
      } else {
        // Aynı canonical event için backend farklı alias satırları gönderebilir.
        // Bu durumda toplamak double-count üretir; en yüksek değeri koruyoruz.
        prev.count = Math.max(prev.count, count);
        prev.uniqueUsers = Math.max(prev.uniqueUsers, uniqueUsers);
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [stats]);

  /**
   * Frontend dateRange'i backend formatına çevir
   * Frontend: 'today' | 'weekly' | 'monthly' | 'yearly' | 'all'
   * Backend: 'today' | 'week' | 'month' | 'year' | 'all'
   */
  const getBackendDateRange = useCallback((frontendDateRange) => {
    switch (frontendDateRange) {
      case 'today':
        return 'today';
      case 'weekly':
        return 'week';
      case 'monthly':
        return 'month';
      case 'yearly':
        return 'year';
      case 'all':
        return 'all'; // Dashboard ile tutarlı olması için 'all' gönder
      default:
        return 'today';
    }
  }, []);

  /**
   * İstatistikleri getir
   * ✅ Pending request kontrolü ile duplicate request'leri önler
   */
  const fetchStats = useCallback(async () => {
    // Eğer zaten bir request pending ise, yeni request gönderme
    if (fetchStatsRef.current) {
      return fetchStatsRef.current; // Mevcut promise'i döndür
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Frontend dateRange'i backend formatına çevir
      const backendDateRange = getBackendDateRange(dateRange);
      
      // Request promise'ini sakla (axiosManager'daki duplicate request önleme de çalışacak)
      const requestPromise = activityService.getStats({ dateRange: backendDateRange });
      fetchStatsRef.current = requestPromise;
      
      const result = await requestPromise;
      
      // ✅ Request tamamlandı, ref'i temizle (hata olsa bile)
      fetchStatsRef.current = null;
      
      if (result.success) {
        if (result.data && Array.isArray(result.data) && result.data.length > 0) {
          setStats(result.data);
        } else if (result.data && !Array.isArray(result.data)) {
          // Eğer data bir obje ise, array'e çevirmeyi dene
          if (typeof result.data === 'object' && result.data !== null) {
            const dataArray = Object.values(result.data);
            setStats(dataArray);
          } else {
            setStats([]);
          }
        } else {
          setStats([]);
        }
        
        // ✅ Backend'den gelen summary.totalUniqueUsers'ı kullan
        if (result.summary?.totalUniqueUsers !== null && result.summary?.totalUniqueUsers !== undefined) {
          setTotalUniqueUsersFromBackend(result.summary.totalUniqueUsers);
        } else {
          setTotalUniqueUsersFromBackend(null);
        }
        
        // ✅ Summary'yi parent component'e gönder (Dashboard için ageGroups ve genderStats)
        if (onSummaryChange && result.summary) {
          onSummaryChange(result.summary);
        }
      } else {
        setError(result.error || 'İstatistikler yüklenemedi');
        setTotalUniqueUsersFromBackend(null);
      }
    } catch (err) {
      setError('İstatistikler yüklenirken bir hata oluştu');
      // ✅ Hata durumunda da ref'i temizle
      fetchStatsRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [dateRange, getBackendDateRange, onSummaryChange]);

  /**
   * WebSocket'ten gelen istatistik güncellemesini işle
   * ✅ Pending request kontrolü ile duplicate request'leri önler
   */
  const handleStatsUpdate = useCallback(() => {
    // Eğer zaten bir request pending ise, yeni request gönderme
    if (!fetchStatsRef.current) {
      fetchStats();
    }
  }, [fetchStats]);

  /**
   * ✅ Birleştirilmiş useEffect - İlk yükleme, dateRange değişikliği ve WebSocket event'leri
   * Duplicate request'leri önlemek için tek bir useEffect kullanıyoruz
   */
  useEffect(() => {
    // Pending request kontrolü - Eğer zaten bir request varsa yeni request gönderme
    if (fetchStatsRef.current) {
      return;
    }

    fetchStats();

    if (socket) {
      socket.on('activity-stats-updated', handleStatsUpdate);

      return () => {
        socket.off('activity-stats-updated', handleStatsUpdate);
      };
    }
    // ✅ dateRange değiştiğinde de çalışır
    // fetchStats ve handleStatsUpdate dependency'de değil çünkü useCallback ile memoize edildiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, dateRange]);

  /**
   * Toplam aktivite sayısı (birleştirilmiş satırlar üzerinden; yüzdeler tutarlı olur)
   */
  const totalActivities = mergedStats.reduce((sum, stat) => sum + (stat.count || 0), 0);

  /**
   * Toplam benzersiz kullanıcı sayısını hesapla
   * ✅ Öncelik: Backend'den gelen summary.uniqueUsers
   * Fallback: Stats'tan hesaplanan değer
   */
  const totalUniqueUsers = totalUniqueUsersFromBackend !== null 
    ? totalUniqueUsersFromBackend 
    : stats.reduce((sum, stat) => sum + (stat.uniqueUsers || 0), 0);

  if (loading && stats.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-center h-[300px]">
          <Lottie animationData={loadingAnimation} loop={true} className="w-32 h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
            <TrendingUp size={20} />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Aktivite İstatistikleri</h3>
            <p className="text-sm text-gray-500 mt-1">Event tipi bazlı analiz</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Özet Kartlar */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-gradient-to-br from-purple-50 to-white rounded-2xl border border-purple-100">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={18} className="text-purple-600" />
            <span className="text-sm font-medium text-gray-600">Toplam Aktivite</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalActivities.toLocaleString('tr-TR')}</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <Users size={18} className="text-blue-600" />
            <span className="text-sm font-medium text-gray-600">Benzersiz Kullanıcı</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalUniqueUsers.toLocaleString('tr-TR')}</p>
        </div>
      </div>

      {/* Detaylı İstatistikler */}
      <div className="space-y-4 max-h-[550px] overflow-y-auto pr-2 custom-scrollbar">
        {mergedStats.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <TrendingUp size={32} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm">Seçilen tarih aralığında aktivite yok</p>
          </div>
        ) : (
          mergedStats.map((stat, index) => {
            const percentage = totalActivities > 0 
              ? ((stat.count || 0) / totalActivities) * 100 
              : 0;

            const uniqueKey = `${stat.eventType || 'unknown'}_${index}`;

            const eventLabel = getActivityEventStatLabel(stat.eventType);

            return (
              <div
                key={uniqueKey}
                className="group p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-1">
                      {eventLabel}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Activity size={14} />
                        {stat.count || 0} kez
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {stat.uniqueUsers || 0} kullanıcı
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-bold text-purple-600">
                      {percentage.toFixed(1)}%
                    </p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-500 group-hover:from-purple-600 group-hover:to-purple-700"
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ActivityStatsWidget;

